import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { useSnackbar } from 'notistack';
import { sendCheckInOtp, validateCheckInOtp } from '../checkInService';
import PrimaryActionButton from '../components/PrimaryActionButton';

function OtpStep({ mobile, onVerified }) {
  const { enqueueSnackbar } = useSnackbar();
  const [otp, setOtp] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isValidatingOtp, setIsValidatingOtp] = useState(false);
  const inputRefs = useRef([]);
  const otpLength = useMemo(() => otp.replace(/\D/g, '').length, [otp]);
  const otpDigits = otp.padEnd(6, ' ').slice(0, 6).split('');

  const sendOtp = useCallback(() => {
    setIsSendingOtp(true);

    sendCheckInOtp(mobile).then(({error}) => {
      setIsSendingOtp(false);

      if(error){
        enqueueSnackbar(error?.message || error || 'Unable to send OTP', {variant: 'error'});
        return;
      }

      enqueueSnackbar('An OTP has been sent to your mobile number.', {variant: 'success'});
    });
  }, [enqueueSnackbar, mobile]);

  useEffect(() => {
    setOtp('');
    sendOtp();
  }, [sendOtp]);

  const updateOtp = (nextDigits) => {
    setOtp(nextDigits.join('').replace(/\D/g, '').slice(0, 6));
  };

  const handleDigitChange = (index) => (event) => {
    const value = event.target.value.replace(/\D/g, '');

    if(!value){
      const nextDigits = [...otpDigits];
      nextDigits[index] = '';
      updateOtp(nextDigits);
      return;
    }

    const nextDigits = [...otpDigits];
    value.slice(0, 6 - index).split('').forEach((digit, offset) => {
      nextDigits[index + offset] = digit;
    });
    updateOtp(nextDigits);

    const nextIndex = Math.min(index + value.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleKeyDown = (index) => (event) => {
    if(event.key !== 'Backspace' || otpDigits[index].trim()){
      return;
    }

    inputRefs.current[Math.max(index - 1, 0)]?.focus();
  };

  const handlePaste = (event) => {
    const pastedOtp = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if(!pastedOtp){
      return;
    }

    event.preventDefault();
    setOtp(pastedOtp);
    inputRefs.current[Math.min(pastedOtp.length, 6) - 1]?.focus();
  };

  const verifyOtp = () => {
    if(otpLength !== 6){
      enqueueSnackbar('Enter a valid 6 digit OTP', {variant: 'error'});
      return;
    }

    setIsValidatingOtp(true);

    validateCheckInOtp({mobile, otp, purpose: 'INMATE_OTP'}).then(({response, error}) => {
      setIsValidatingOtp(false);

      if(error){
        enqueueSnackbar(error?.message || error || 'Unable to validate OTP', {variant: 'error'});
        return;
      }

      enqueueSnackbar(response?.message || 'OTP verified successfully', {variant: 'success'});
      onVerified(otp);
    });
  };

  return (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 'calc(100vh - 160px)' }}>
      <PhoneAndroidIcon sx={{ fontSize: 120, color: 'primary.light', opacity: 0.32, mb: 4 }} />
      <Typography variant="h5" fontWeight={700} textAlign="center">
        Verify your mobile number
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 520, mt: 2, mb: 4, lineHeight: 1.6 }}>
        Enter the OTP sent to {mobile}.
      </Typography>
      <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} justifyContent="center" onPaste={handlePaste}>
        {otpDigits.map((digit, index) => (
          <TextField
            key={index}
            value={digit.trim()}
            onChange={handleDigitChange(index)}
            onKeyDown={handleKeyDown(index)}
            inputRef={(input) => {
              inputRefs.current[index] = input;
            }}
            inputProps={{
              maxLength: 1,
              inputMode: 'numeric',
              'aria-label': `OTP digit ${index + 1}`,
              style: {
                textAlign: 'center',
                fontSize: 24,
                fontWeight: 700,
                padding: '14px 0',
              },
            }}
            sx={{
              width: { xs: 44, sm: 52 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                bgcolor: 'background.paper',
              },
            }}
          />
        ))}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <PrimaryActionButton onClick={verifyOtp} disabled={otpLength !== 6 || isValidatingOtp || isSendingOtp}>
          {isValidatingOtp ? 'Verifying...' : 'Verify'}
        </PrimaryActionButton>
      </Stack>
    </Stack>
  );
}

export default OtpStep;
